'use client';

// TestingPhase - Container component for bot testing phase

import { useState, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import { useTranslations } from 'next-intl';
import { ChatInterface } from './ChatInterface';
import { SuggestedQuestions } from './SuggestedQuestions';
import { SuggestedQuestionsEditor } from './SuggestedQuestionsEditor';
import { ContentCoverageSummary } from './ContentCoverageSummary';
import { AddMoreContentPrompt, type ContentType } from './AddMoreContentPrompt';
import { IntakeQuestionsConfig } from '../intake-questions';
import { useChat } from './useChat';
import { useSuggestedQuestions } from './useSuggestedQuestions';
import { useBot } from '../../hooks/useBot';
import { isOutOfScopeResponse } from '@boty/ai';
import type { TestingPhaseProps } from './types';

// Helper to get/create session ID
function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return crypto.randomUUID();

  const storageKey = 'boty_session_id';
  let sessionId = localStorage.getItem(storageKey);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(storageKey, sessionId);
  }
  return sessionId;
}

/**
 * TestingPhase - Main container component for bot testing
 *
 * Features:
 * - Content coverage summary (topics the bot knows)
 * - Suggested questions (exactly 3)
 * - Chat interface with streaming responses
 * - Add more content prompt when bot can't answer
 * - Navigation buttons (back, add content, done)
 * - Mobile-responsive layout
 */
export function TestingPhase({
  botId,
  botName,
  onAddContent,
  onComplete,
  onBack,
  contentVersion = 0,
  onQuestionCountChange,
}: TestingPhaseProps) {
  const t = useTranslations('creator.testing');
  const tIntake = useTranslations('creator.intakeQuestions');
  const tClarification = useTranslations('creator.clarification');

  // Story 7.6: Intake Questions CTA
  const [intakeDialogOpen, setIntakeDialogOpen] = useState(false);

  // Session management
  const [sessionId] = useState(getOrCreateSessionId);

  // Chat state
  const { messages, isLoading, error, sendMessage } = useChat({
    botId,
    sessionId,
  });

  // Scroll to top when testing phase mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track question count for engagement prompts (Story 3.8)
  useEffect(() => {
    if (onQuestionCountChange) {
      const userMessageCount = messages.filter((m) => m.role === 'user').length;
      onQuestionCountChange(userMessageCount);
    }
  }, [messages, onQuestionCountChange]);

  // Suggested questions
  const {
    suggestions,
    topics,
    isLoading: isSuggestionsLoading,
    refetch: refetchSuggestions,
  } = useSuggestedQuestions({ botId });

  // Story 7.9: bot record drives the SuggestedQuestionsEditor enable/disable toggle.
  const { data: botResponse } = useBot(botId);
  const suggestedQuestionsEnabled = botResponse?.data?.suggestedQuestionsEnabled ?? true;

  // Refresh suggestions when content version changes (content was added)
  useEffect(() => {
    if (contentVersion > 0) {
      refetchSuggestions();
    }
  }, [contentVersion, refetchSuggestions]);

  // Input value state (for suggested question population)
  const [inputValue, setInputValue] = useState('');

  // Track which suggested questions have been used (clicked)
  const [usedQuestions, setUsedQuestions] = useState<string[]>([]);

  // Track last assistant message for out-of-scope detection
  const [showAddContentPrompt, setShowAddContentPrompt] = useState(false);

  // Check for out-of-scope responses
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (
      lastMessage?.role === 'assistant' &&
      !lastMessage.isStreaming &&
      isOutOfScopeResponse(lastMessage.content)
    ) {
      setShowAddContentPrompt(true);
    } else if (lastMessage?.role === 'user') {
      setShowAddContentPrompt(false);
    }
  }, [messages]);

  // Handle suggested question click - send immediately and mark as used
  const handleSuggestionSelect = useCallback(
    (question: string) => {
      setUsedQuestions((prev) => [...prev, question]);
      sendMessage(question);
    },
    [sendMessage]
  );

  // Handle input change
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  // Handle send message
  const handleSendMessage = useCallback(
    (content: string) => {
      sendMessage(content);
      setInputValue('');
    },
    [sendMessage]
  );

  // Handle add content button - passes content type through to parent
  const handleAddContent = useCallback(
    (type?: ContentType) => {
      if (onAddContent) {
        onAddContent(type);
      }
    },
    [onAddContent]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 1.5,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('subtitle')}
          </Typography>
          {botName && (
            <Typography
              variant="h6"
              color="primary"
              sx={{ mt: 1, fontWeight: 'medium' }}
            >
              {t('botReady')}: {botName}
            </Typography>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Content coverage summary */}
      <ContentCoverageSummary
        topics={topics}
        isLoading={isSuggestionsLoading}
      />

      {/* Intake Questions Dialog */}
      <Dialog open={intakeDialogOpen} onClose={() => setIntakeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {tIntake('sectionTitle')}
          <IconButton
            aria-label="close"
            onClick={() => setIntakeDialogOpen(false)}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <IntakeQuestionsConfig
            botId={botId}
            intakeEnabled={false}
            intakeCompletionMessage={null}
            intakePromptMessage={null}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIntakeDialogOpen(false)}>
            {tClarification('done')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat interface */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ChatInterface
          messages={messages}
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          error={error}
          showTypingIndicator={isLoading && !messages.some((m) => m.isStreaming)}
          inputValue={inputValue}
          onInputChange={handleInputChange}
        />
      </Box>

      {/* Suggested questions (read-only chips for the test chat) */}
      <SuggestedQuestions
        suggestions={suggestions}
        isLoading={isSuggestionsLoading}
        onSelect={handleSuggestionSelect}
        usedQuestions={usedQuestions}
      />

      {/* Story 7.9: Customize what consumers see (collapsed by default during creation) */}
      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TuneIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2">
              {t('customizeSuggestionsTitle')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <SuggestedQuestionsEditor
            botId={botId}
            enabledInitial={suggestedQuestionsEnabled}
            hasContent={suggestions.length > 0 || topics.length > 0}
          />
        </AccordionDetails>
      </Accordion>

      {/* Add content prompt (shown when bot can't answer) */}
      {showAddContentPrompt && onAddContent && (
        <AddMoreContentPrompt onAddContent={handleAddContent} />
      )}

      {/* Story 7.6: Intake Questions CTA (always visible as entry point) */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
          <PersonAddIcon color="primary" sx={{ flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2">{tIntake('ctaTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">{tIntake('ctaDescription')}</Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setIntakeDialogOpen(true)}
          sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
        >
          {tIntake('ctaButton')}
        </Button>
      </Paper>

      {/* Desktop: inline action buttons */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Divider />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mt: 2,
          }}
        >
          {/* Back button */}
          {onBack && (
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
            >
              {t('actions.back')}
            </Button>
          )}

          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            {/* Add more content button */}
            {onAddContent && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleAddContent()}
              >
                {t('actions.addMoreContent')}
              </Button>
            )}

            {/* Done testing button */}
            {onComplete && (
              <Button
                variant="contained"
                startIcon={<ShareIcon />}
                onClick={onComplete}
              >
                {t('actions.readyToShare')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Mobile: floating footer */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          bottom: 0,
          // Use logical properties so the bar spans full width in RTL too.
          insetInlineStart: 0,
          insetInlineEnd: 0,
          p: 2,
          gap: 1.5,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          zIndex: 1100,
        }}
      >
        {/* Add more content button */}
        {onAddContent && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddContent()}
            sx={{ flex: 1 }}
          >
            {t('actions.addMoreContent')}
          </Button>
        )}

        {/* Done testing button */}
        {onComplete && (
          <Button
            variant="contained"
            startIcon={<ShareIcon />}
            onClick={onComplete}
            sx={{ flex: 1 }}
          >
            {t('actions.readyToShare')}
          </Button>
        )}
      </Box>

      {/* Spacer to prevent content from being hidden behind floating footer on mobile */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, height: 72 }} />
    </Box>
  );
}

export default TestingPhase;
